import "./track.css";

export type RouteStop = {
  label: string;
  /** Small mono number above the label, e.g. "01". */
  num?: string;
  /** Makes the stop a link (e.g. "#web" jump links). */
  href?: string;
};

type RouteLineProps = {
  stops: RouteStop[];
  /** "lg" = brand track (thick, dotted centre) for navigation; "sm" = thin line. */
  size?: "sm" | "lg";
  /** Accessible name — use it for navigation routes. */
  label?: string;
  /** Text appended to the last stop, e.g. "↺". */
  endSuffix?: string;
};

/**
 * A route map: stops as station dots on a navy line, the last stop in coral.
 * Replaces pill bars for deliverables and jump links. Horizontal on desktop,
 * vertical (line down the left) on phones.
 */
const RouteLine = ({ stops, size = "sm", label, endSuffix }: RouteLineProps) => {
  const list = (
    <ol className={`is-route is-route--${size}`} aria-label={label}>
      {stops.map((stop, i) => {
        const last = i === stops.length - 1;
        const content = (
          <>
            <span className="is-route__dot" aria-hidden="true" />
            {stop.num && <span className="is-route__num">{stop.num}</span>}
            <span className="is-route__label">
              {stop.label}
              {last && endSuffix ? ` ${endSuffix}` : ""}
            </span>
          </>
        );
        return (
          <li key={stop.label} className={"is-route__stop" + (last ? " is-route__stop--end" : "")}>
            {stop.href ? (
              <a href={stop.href} className="is-route__link">
                {content}
              </a>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ol>
  );

  return stops.some((s) => s.href) ? <nav aria-label={label}>{list}</nav> : list;
};

export default RouteLine;
